<script setup>
import {
  computed,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const emit = defineEmits(['click']);
const props = defineProps({
  label: {
    type: String,
    default: '',
  },
  rawLabel: {
    type: String,
    required: false,
  },
  icon: {
    type: String,
    default: '',
  },
  iconImage: {
    type: String,
    default: '',
  },
  progress: {
    type: Number,
    default: null,
  },
  type: {
    type: String,
    default: 'button',
  },
  buttonStyle: {
    type: String,
    default: 'primary',
  },
  block: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'md',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  routeTo: {
    type: Object,
    required: false,
  },
  href: {
    type: String,
    required: false,
  },
  target: {
    type: String,
    default: '_self',
  },
  reverse: {
    type: Boolean,
    default: false,
  },
  tooltip: {
    type: String,
    required: false,
  },
  tooltipPlacement: {
    type: String,
    default: 'top',
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  showDot: {
    type: Boolean,
    default: false,
  },
  testId: {
    type: String,
    required: false,
  },
});
const hover = ref(false);

const buttonClass = computed(
  () => {
    return [
      `btn-${props.buttonStyle}`,
      `btn-${props.size}`,
      props.block ? 'w-100' : '',
      props.reverse ? 'flex-row-reverse' : '',
    ];
  },
)

const iconClass = computed(
  () => {
    const marginClass = props.reverse ? 'ms-2' : 'me-2';
    return [
      `fa-${props.icon}`,
      props.label || props.rawLabel ? marginClass : '',
    ];
  },
)

const isSafeIconPath = (iconPath) => (
  /^[a-zA-Z0-9/_-]+\.[a-zA-Z0-9]+$/.test(iconPath)
  && !iconPath.includes('..')
  && !iconPath.startsWith('/')
);

const iconImageSrc = computed(() => {
  if (!props.iconImage || !isSafeIconPath(props.iconImage)) {
    return null;
  }
  try {
    return require(`@/assets/icons/${props.iconImage}`);
  } catch {
    return null;
  }
});

const PROGRESS_RING_RADIUS = 15.5;
const PROGRESS_RING_CIRCUMFERENCE = 2 * Math.PI * PROGRESS_RING_RADIUS;

const hasProgress = computed(() => props.progress !== null && props.progress !== undefined);

const progressDashOffset = computed(() => {
  const clamped = Math.max(0, Math.min(100, props.progress || 0));
  return PROGRESS_RING_CIRCUMFERENCE * (1 - clamped / 100);
});

const tooltipConfig = computed(() => {
  if (props.loading && !hover.value) {
    return null;
  }
  return props.tooltip ? { label: t(props.tooltip), placement: props.tooltipPlacement } : null
});
</script>

<template>
  <button
    v-if="isDisabled"
    class="btn text-nowrap d-flex align-items-center justify-content-center m-0"
    :class="buttonClass"
    :type="props.type"
    :id="props.testId || undefined"
    @click="() => {}"
    v-tooltip="tooltipConfig"
    disabled
  >
    <svg
      v-if="hasProgress"
      class="button-progress-ring"
      :class="{ 'ms-2': reverse && (label || rawLabel), 'me-2': !reverse && (label || rawLabel) }"
      viewBox="0 0 36 36"
      aria-hidden="true"
    >
      <circle class="button-progress-ring-bg" cx="18" cy="18" r="15.5" />
      <circle
        class="button-progress-ring-fg"
        cx="18" cy="18" r="15.5"
        :stroke-dasharray="PROGRESS_RING_CIRCUMFERENCE"
        :stroke-dashoffset="progressDashOffset"
      />
    </svg>
    <img
      v-else-if="iconImageSrc"
      :src="iconImageSrc"
      class="button-icon-image"
      :class="{ 'ms-2': reverse && (label || rawLabel), 'me-2': !reverse && (label || rawLabel) }"
      alt=""
    >
    <i
      v-else-if="icon"
      class="fa"
      :class="iconClass"
    ></i>

    <span v-if="rawLabel">
      {{ rawLabel }}
    </span>
    <span v-else-if="label">{{ $t(label) }}</span>
    <span
      v-if="showDot"
      class="button-dot attention-dot"
    />
  </button>
  <router-link
    v-else-if="routeTo"
    class="btn text-nowrap m-0"
    :class="buttonClass"
    :type="type"
    :to="routeTo"
    :id="props.testId || undefined"
    v-tooltip="tooltipConfig"
  >
    <svg
      v-if="hasProgress"
      class="button-progress-ring"
      :class="{ 'ms-2': reverse && (label || rawLabel), 'me-2': !reverse && (label || rawLabel) }"
      viewBox="0 0 36 36"
      aria-hidden="true"
    >
      <circle class="button-progress-ring-bg" cx="18" cy="18" r="15.5" />
      <circle
        class="button-progress-ring-fg"
        cx="18" cy="18" r="15.5"
        :stroke-dasharray="PROGRESS_RING_CIRCUMFERENCE"
        :stroke-dashoffset="progressDashOffset"
      />
    </svg>
    <img
      v-else-if="iconImageSrc"
      :src="iconImageSrc"
      class="button-icon-image"
      :class="{ 'ms-2': reverse && (label || rawLabel), 'me-2': !reverse && (label || rawLabel) }"
      alt=""
    >
    <i
      v-else-if="icon"
      class="fa"
      :class="iconClass"
    ></i>

    <span v-if="rawLabel">
      {{ rawLabel }}
    </span>
    <span v-else-if="label">{{ $t(label) }}</span>
    <span
      v-if="showDot"
      class="button-dot attention-dot"
    />
  </router-link>
  <a
    v-else-if="href"
    class="btn text-nowrap m-0"
    :class="buttonClass"
    :type="type"
    :href="href"
    :target="target"
    :id="props.testId || undefined"
    v-tooltip="tooltipConfig"
  >
    <svg
      v-if="hasProgress"
      class="button-progress-ring"
      :class="{ 'ms-2': reverse && (label || rawLabel), 'me-2': !reverse && (label || rawLabel) }"
      viewBox="0 0 36 36"
      aria-hidden="true"
    >
      <circle class="button-progress-ring-bg" cx="18" cy="18" r="15.5" />
      <circle
        class="button-progress-ring-fg"
        cx="18" cy="18" r="15.5"
        :stroke-dasharray="PROGRESS_RING_CIRCUMFERENCE"
        :stroke-dashoffset="progressDashOffset"
      />
    </svg>
    <img
      v-else-if="iconImageSrc"
      :src="iconImageSrc"
      class="button-icon-image"
      :class="{ 'ms-2': reverse && (label || rawLabel), 'me-2': !reverse && (label || rawLabel) }"
      alt=""
    >
    <i
      v-else-if="icon"
      class="fa"
      :class="iconClass"
    ></i>

    <span v-if="rawLabel">
      {{ rawLabel }}
    </span>
    <span v-else-if="label">{{ $t(label) }}</span>
    <span
      v-if="showDot"
      class="button-dot attention-dot"
    />
  </a>
  <button
    v-else
    class="btn text-nowrap d-flex align-items-center justify-content-center m-0"
    :class="buttonClass"
    :type="props.type"
    :disabled="loading"
    :id="props.testId || undefined"
    @click="() => !loading ? emit('click') : null"
    v-tooltip="tooltipConfig"
  >
    <i
      :class="{'me-1': !!label}"
      class="spinner-border spinner-border-sm"
      v-if="loading"
      role="status"
      aria-hidden="true"
    />

    <svg
      v-else-if="hasProgress"
      class="button-progress-ring"
      :class="{ 'ms-2': reverse && (label || rawLabel), 'me-2': !reverse && (label || rawLabel) }"
      viewBox="0 0 36 36"
      aria-hidden="true"
    >
      <circle class="button-progress-ring-bg" cx="18" cy="18" r="15.5" />
      <circle
        class="button-progress-ring-fg"
        cx="18" cy="18" r="15.5"
        :stroke-dasharray="PROGRESS_RING_CIRCUMFERENCE"
        :stroke-dashoffset="progressDashOffset"
      />
    </svg>
    <img
      v-else-if="iconImageSrc"
      :src="iconImageSrc"
      class="button-icon-image"
      :class="{ 'ms-2': reverse && (label || rawLabel), 'me-2': !reverse && (label || rawLabel) }"
      alt=""
    >
    <i
      v-else-if="icon"
      class="fa"
      :class="iconClass"
    ></i>


    <span v-if="rawLabel">
      {{ rawLabel }}
    </span>
    <span v-else-if="label">{{ $t(label) }}</span>
    <span
      v-if="showDot"
      class="button-dot attention-dot"
    />
  </button>
</template>

<style scoped>
.button-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: var(--bs-danger, #dc3545);
  margin-left: 0.4rem;
  vertical-align: middle;
}

.attention-dot {
  animation: attention-dot-pulse 1.6s ease-in-out infinite;
}

@keyframes attention-dot-pulse {
  0%,
  100% {
    transform: translateY(0);
    opacity: 0.9;
  }
  50% {
    transform: translateY(-1px);
    opacity: 1;
  }
}

.button-icon-image {
  width: 14px;
  height: 14px;
  object-fit: contain;
}

.button-progress-ring {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  transform: rotate(-90deg);
}

.button-progress-ring-bg {
  fill: none;
  stroke: currentColor;
  opacity: 0.35;
  stroke-width: 4;
}

.button-progress-ring-fg {
  fill: none;
  stroke: currentColor;
  stroke-width: 4;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.1s linear;
}
</style>
